import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { SubHeader } from 'cstar-react-primitives/lib/redux-form/layout/Container';
import { injectIntl, intlShape } from 'react-intl';
import { fromJS, List as IList } from 'immutable';
import Big from 'big.js';

import { getDiscountRewardAmount } from '../promo/promoSelectors';
import itemMessages from '../item/itemMessages';
import { getAccessoryCostDisplay } from '../accessories/accessoriesUtils';
import { getFormattedAmountWithCurrency } from '../utils/numberUtils';
import {
    hasPlasticItems,
    hasPromoItems,
    getPaidCardsCount,
    getPromoLineItems,
    getItemId,
    getItemQuantity,
    getItemDenomination,
    getItemLabel,
    getSubTotal,
    getShippingTotal,
    getGrandTotalCostMinusFees,
    getIsNonDenomItem,
    getItemsAccessoryCount,
    getItemAccessoriesGrandTotalCost,
    getItemCurrency
} from '../item/itemSelectors';
import { getShippingCostDisplay } from '../shippingMethod/shippingMethodUtils';
import { getCartCurrencyCode } from '../payment/paymentSelectors';

import ItemsSummaryLineItem from './layout/ItemsSummaryLineItem';

import { useConsumerFees } from '../fees/ConsumerFeesProvider';
import { feeTypes, getFeeAmountByTypeAndCost, getFeeTypeFromItem } from '../fees/feesUtils';
import {
    setActivationFeesCount as setActivationFeesCountAction,
    setActivationFeesTotal as setActivationFeesTotalAction,
    setConsumerFees,
    setGrandTotal as setGrandTotalAction,
    setUyopFeesTotal as setUyopFeesTotalAction
} from '../payment/paymentModule';
import { setPaypalHandlingTotal } from '../paypal/paypalModule';

export const PromoItemsSummary = ({ intl, promoItems }) => {
    const promoLineItems = promoItems.map((promo, index) => {
        const promoLabel = getItemLabel(promo);
        const promoValue = getItemDenomination(promo);
        const promoCardsCount = getItemQuantity(promo);
        const promoCurrency = getItemCurrency(promo);

        let message;
        if (getIsNonDenomItem(promo)) {
            message = intl.formatMessage(itemMessages.orderTotalNonDenomLabel, {
                label: promoLabel,
                count: promoCardsCount
            });
        } else {
            message = intl.formatMessage(itemMessages.orderTotalSpecialOffers, {
                denom: getFormattedAmountWithCurrency(intl, promoValue, promoCurrency, true),
                label: promoLabel,
                count: promoCardsCount
            });
        }

        return (
            <ItemsSummaryLineItem
                key={getItemId(promo)}
                message={message}
                value={intl.formatMessage(itemMessages.orderTotalBonusValue)}
                cashbotDataName={`promo-summary-lineitem-${index}`}
                isEmphasized
            />
        );
    });

    return (
        <div>
            { promoLineItems }
        </div>
    );
};

PromoItemsSummary.propTypes = {
    intl: intlShape.isRequired,
    promoItems: PropTypes.object.isRequired
};

export const PureItemsSummary = ({
    intl,
    hasPlastic,
    hasPromo,
    paidCardsCount,
    subtotal,
    cartShippingTotal,
    promoItems,
    currency,
    totalDiscountRewardAmount,
    accessoriesCount,
    accessoriesTotalCost,
    storedGrandTotal,
    items
}) => {
    const { consumerFees, requestConsumerFees } = useConsumerFees();
    const [activationFeeTotals, setActivationFeeTotals] = useState({ totalAmount: 0, totalCount: 0 });
    const [uyoFeeTotals, setUyoFeeTotals] = useState({ totalAmount: 0, totalCount: 0 });
    const [grandTotal, setGrandTotal] = useState(new Big(storedGrandTotal));

    
    const grandTotalWithoutFees = useSelector(state => getGrandTotalCostMinusFees(state));
    const dispatch = useDispatch();

    
    
    
    useEffect(() => {
        dispatch(setConsumerFees(consumerFees));
    }, [consumerFees]);

    useEffect(() => {
        dispatch(setGrandTotalAction(grandTotal));
    }, [grandTotal]);

    useEffect(() => {
        
        if (!consumerFees || !consumerFees.length > 0) {
            requestConsumerFees(currency);
        }
    }, []);

    useEffect(() => {
        
        let activationFeeTotal = 0;
        let activationFeeCount = 0;
        let uyoFeeTotal = 0;
        let uyoFeeCount = 0;
        let isComponentMounted = true;
        items.filter(item => !item.get('isRemoved')).forEach((item) => {
            const feeType = getFeeTypeFromItem(item);
            const programCode = item.get('programCode');
            const amount = Number(item.get('amount'));
            let uyopFeeAmount = 0;
            let activationFeeAmount = 0;
            if (feeType === feeTypes.UYOP) {
                uyopFeeAmount = getFeeAmountByTypeAndCost(consumerFees,
                    feeType,
                    amount,
                    programCode);
                
                activationFeeAmount = getFeeAmountByTypeAndCost(consumerFees,
                    'PLAC',
                    amount,
                    programCode);
            } else {
                activationFeeAmount = getFeeAmountByTypeAndCost(consumerFees,
                    feeType,
                    amount,
                    programCode);
            }
            const quantity = item.get('quantity');
            if (uyopFeeAmount) {
                uyoFeeCount += quantity;
                uyoFeeTotal += (quantity * uyopFeeAmount);
            }
            if (activationFeeAmount) {
                activationFeeCount += quantity;
                activationFeeTotal += (quantity * activationFeeAmount);
            }
        });

        if (isComponentMounted) {
            setActivationFeeTotals({ totalAmount: activationFeeTotal, totalCount: activationFeeCount });
            setUyoFeeTotals({ totalAmount: uyoFeeTotal, totalCount: uyoFeeCount });
            
            
            if (!storedGrandTotal) {
                const grandTotalAmount = grandTotalWithoutFees.add(activationFeeTotal).add(uyoFeeTotal);
                setGrandTotal(grandTotalAmount);
                
                const feesTotal = activationFeeTotal + uyoFeeTotal;
                dispatch(setPaypalHandlingTotal(feesTotal));
                dispatch(setActivationFeesTotalAction(activationFeeTotal));
                dispatch(setUyopFeesTotalAction(uyoFeeTotal));
                dispatch(setActivationFeesCountAction(activationFeeCount));
            }
        }

        return () => {
            isComponentMounted = false;
        };
    }, [consumerFees, items, totalDiscountRewardAmount, cartShippingTotal]);

    const containerProps = {
        className: 'items-summary',
        role: 'region',
        'aria-label': intl.formatMessage(itemMessages.orderSummaryHeader)
    };
    const hasDiscount = totalDiscountRewardAmount < 0;
    const shippingTotalValue = getShippingCostDisplay(cartShippingTotal, intl, currency);
    const hasAccessories = accessoriesCount > 0;
    const accessoriesTotalCostFormatted = getAccessoryCostDisplay(accessoriesTotalCost, intl, currency);

    return (
        <section className="items-summary-container">
            <SubHeader heading={intl.formatMessage(itemMessages.orderSummaryHeader)} />
            <div {...containerProps}>
                <ItemsSummaryLineItem
                    message={intl.formatMessage(itemMessages.orderSummaryCurrencyLabel)}
                    value={currency}
                    cashbotDataName='currency'
                />
                <ItemsSummaryLineItem
                    message={intl.formatMessage(itemMessages.orderTotalSubTotal, { count: paidCardsCount })}
                    value={getFormattedAmountWithCurrency(intl, subtotal, currency, true)}
                    cashbotDataName='subtotal'
                />
                { hasAccessories && (
                    <ItemsSummaryLineItem
                        message={intl.formatMessage(itemMessages.orderTotalAccessories, { count: accessoriesCount })}
                        value={accessoriesTotalCostFormatted}
                        cashbotDataName='accessories'
                    />
                )}
                { hasPlastic && (
                    <div data-testid="shipping-summary">
                        <ItemsSummaryLineItem
                            message={intl.formatMessage(itemMessages.orderTotalEstShipping)}
                            value={shippingTotalValue}
                            cashbotDataName='shipping'
                        />
                    </div>
                ) }
                { activationFeeTotals.totalAmount !== 0 && (
                    <ItemsSummaryLineItem
                        message={intl.formatMessage(
                            itemMessages.orderTotalActivationFees, { count: activationFeeTotals.totalCount }
                        )}
                        value={getFormattedAmountWithCurrency(intl, activationFeeTotals.totalAmount, currency, true)}
                        cashbotDataName='activationFees'
                    />
                ) }
                { uyoFeeTotals.totalAmount !== 0 && (
                    <ItemsSummaryLineItem
                        message={intl.formatMessage(itemMessages.orderTotalUYOPlasticFee,
                            { count: uyoFeeTotals.totalCount })}
                        value={getFormattedAmountWithCurrency(intl, uyoFeeTotals.totalAmount, currency, true)}
                        cashbotDataName='uyoFees'
                    />
                ) }
                { hasDiscount && (
                    <ItemsSummaryLineItem
                        message={intl.formatMessage(itemMessages.orderTotalDiscount)}
                        value={getFormattedAmountWithCurrency(intl, totalDiscountRewardAmount, currency, true)}
                        cashbotDataName='discount'
                    />
                )}
                { hasPromo && (
                    <div data-testid="promo-summary">
                        <PromoItemsSummary
                            intl={intl}
                            promoItems={promoItems}
                        />
                    </div>
                ) }
                <ItemsSummaryLineItem
                    message={intl.formatMessage(itemMessages.orderTotalOrderTotal)}
                    value={getFormattedAmountWithCurrency(intl, grandTotal, currency, true)}
                    cashbotDataName='grandTotal'
                    isLarge
                />
            </div>
        </section>
    );
};

PureItemsSummary.defaultProps = {
    hasPlastic: false,
    hasPromo: false,
    storedGrandTotal: 0,
    items: fromJS([])
};

PureItemsSummary.propTypes = {
    intl: intlShape.isRequired,
    hasPlastic: PropTypes.bool,
    hasPromo: PropTypes.bool,
    paidCardsCount: PropTypes.number.isRequired,
    subtotal: PropTypes.number.isRequired,
    promoItems: PropTypes.instanceOf(IList).isRequired,
    cartShippingTotal: PropTypes.number.isRequired,
    storedGrandTotal: PropTypes.number,
    currency: PropTypes.string.isRequired,
    totalDiscountRewardAmount: PropTypes.number.isRequired,
    accessoriesCount: PropTypes.number.isRequired,
    accessoriesTotalCost: PropTypes.instanceOf(Big).isRequired,
    items: PropTypes.instanceOf(IList)
};

const mapStateToProps = state => ({
    hasPlastic: hasPlasticItems(state),
    hasPromo: hasPromoItems(state),
    paidCardsCount: getPaidCardsCount(state),
    subtotal: parseFloat(getSubTotal(state).toFixed(2)),
    promoItems: getPromoLineItems(state),
    cartShippingTotal: getShippingTotal(state),
    grandTotal: parseFloat(getGrandTotalCostMinusFees(state).toFixed(2)),
    totalDiscountRewardAmount: getDiscountRewardAmount(state),
    accessoriesCount: getItemsAccessoryCount(state),
    accessoriesTotalCost: getItemAccessoriesGrandTotalCost(state),
    currency: getCartCurrencyCode(state)
});


export const ItemsSummary = injectIntl(PureItemsSummary);


export default connect(mapStateToProps)(ItemsSummary);
